import type { DateTime } from "luxon";
import {
	type AutoApproveRule,
	type Nurse,
	OutreachAnswer,
	OutreachApproval,
	type OutreachCountedFactorKind,
	type OutreachExcludedGroup,
	type OutreachExcludedPerson,
	OutreachExclusionFilter,
	type OutreachFactor,
	OutreachFactorKind,
	type OutreachFactorStrength,
	OutreachLevel,
	OutreachMatch,
	type OutreachPlainFactorKind,
	type OutreachPlannedBatch,
	type OutreachPlannedCall,
	OutreachReachedKind,
	type OutreachReachedPerson,
	type OutreachScoredPerson,
	OutreachSection,
	type OutreachSentBatch,
	type OutreachSignUp,
	type OutreachSignUpCheck,
	OutreachVia,
	RequestResponsesStatusOptions,
	type ShiftRequest,
	ShiftType,
} from "../../utils/models";
import { CALL_GAP_MINUTES } from "../../utils/outreachRules";
import { hashString } from "../../utils/utilFunctions";
import { type NurseFacts, type RosterSplit, WEEKLY_OVERTIME_HOURS } from "./demoOutreachRoster";

/**
 * DEMO ONLY (APP-933). Seeds a request's outreach from the real request and the department's real roster, and fills
 * the rest deterministically from the request id, so it stays stable across reloads. APP-918 deletes this folder.
 */

const SEND_HOUR = 10;
/** Outreach starts this many days before the shift, and the demo shows at least this many days of it. */
const LEAD_DAYS = 14;
const MIN_HISTORY_DAYS = 3;
/** When each level starts on its own, counted back from the shift start. */
const CALLS_DAYS_BEFORE = 2;
const OVERTIME_DAYS_BEFORE = 1;
const AGENCY_HOURS_BEFORE = 8;
/** How many of the people who fit just took a shift or never have, so we ask them last. */
const ASK_LAST_PEOPLE = 1;
/** Seeded factors: how often someone's last acceptance was recent enough to help strongly, and how many helps and drags. */
const STRONG_SEEDED_SHARE = 0.2;
const MAX_SEEDED_HELPS = 2;
const MAX_SEEDED_DRAGS = 1;
/** Real weekly-hours and work-history factors: low and high weeks, and how often counts as "worked these hours" strongly. */
const LOW_WEEK_HOURS = 24;
const HIGH_WEEK_HOURS = 32;
const STRONG_WORKED_COUNT = 4;
/** Share of the people we asked who said no; most of the rest just don't answer. */
const DECLINED_PERCENT = 25;
type MinuteRange = { min: number; max: number };
/** How long after we reached them someone signs up, and how long a manager then takes to decide. */
const SIGN_UP_AFTER_MINUTES: MinuteRange = { min: 15, max: 240 };
const MANAGER_DECIDES_AFTER_MINUTES: MinuteRange = { min: 60, max: 480 };
/** A sign-up's week, for the approval check: hours with this shift, and how often it would mean overtime or a clash. */
const SIGN_UP_WEEK_HOURS = [16, 24, 32] as const;
const SIGN_UP_OVERTIME_HOURS = [4, 8] as const;
const SIGN_UP_OVERTIME_SHARE = 0.25;
const SIGN_UP_CONFLICT_SHARE = 0.1;

/** A seeded factor's shape; a shift-type preference is never seeded, since the schedule answers it (`realFactorsOf`). */
type Template = { section: OutreachSection; strength: OutreachFactorStrength; family: string } & (
	| { kind: OutreachCountedFactorKind; counts: readonly number[] }
	| { kind: OutreachPlainFactorKind }
);

const counted = (
	section: OutreachSection,
	kind: OutreachCountedFactorKind,
	strength: OutreachFactorStrength,
	family: string,
	counts: readonly number[],
): Template => ({ section, kind, strength, family, counts });

const plain = (section: OutreachSection, kind: OutreachPlainFactorKind, strength: OutreachFactorStrength, family: string): Template => ({
	section,
	kind,
	strength,
	family,
});

const S = OutreachSection;
const K = OutreachFactorKind;
const STRONG_HELPS: readonly Template[] = [
	counted(S.WorkHistory, K.WorkedSameHours, 2, "worked", [4, 5]),
	counted(S.Acceptance, K.LastAcceptedDaysAgo, 2, "last", [4, 5, 7]),
];
const HELPS: readonly Template[] = [
	plain(S.Acceptance, K.AcceptsMostOffers, 1, "rate"),
	plain(S.NotificationLoad, K.FewNotifications, 1, "notif"),
	plain(S.RecurringShifts, K.FewRecurringShifts, 1, "recurring"),
	counted(S.WeeklyHours, K.LowHoursThatWeek, 1, "hours", [16, 24]),
	counted(S.WorkHistory, K.WorkedSameHours, 1, "worked", [2, 3]),
	plain(S.Acceptance, K.AcceptingLessThanUsual, 1, "pace"),
	counted(S.Acceptance, K.LastAcceptedDaysAgo, 1, "last", [10, 12, 14, 16]),
];
const DRAGS: readonly Template[] = [
	counted(S.WeeklyHours, K.HighHoursThatWeek, -1, "hours", [32, 36]),
	counted(S.NotificationLoad, K.NotificationsThisWeek, -1, "notif", [3, 4]),
	plain(S.Acceptance, K.AcceptingMoreThanUsual, -1, "pace"),
	counted(S.Acceptance, K.LastAcceptedWeeksAgo, -1, "last", [3]),
	plain(S.WorkHistory, K.NotWorkedHoursLately, -1, "worked"),
	plain(S.RecurringShifts, K.ManyRecurringShifts, -1, "recurring"),
	plain(S.Acceptance, K.AcceptsFewOffers, -1, "rate"),
	counted(S.WeeklyHours, K.OvertimeThatWeek, -2, "hours", [4, 8]),
];
const ASK_LAST: readonly Template[] = [plain(S.Acceptance, K.AcceptedYesterday, -2, "last"), plain(S.Acceptance, K.NeverAccepted, -2, "last")];
const ASK_LAST_KINDS: ReadonlySet<OutreachFactorKind> = new Set(ASK_LAST.map((template) => template.kind));
/** Just took a shift, or never has: asked last whatever else helps. */
const isAskLast = (factors: readonly OutreachFactor[]): boolean => factors.some((factor) => ASK_LAST_KINDS.has(factor.kind));

export type Rng = () => number;

/** Mulberry32 seeded from a string: the same seed always gives the same sequence. */
export const rngOf = (seed: string): Rng => {
	let state = hashString(seed) >>> 0;
	return () => {
		state = (state + 0x6d2b79f5) >>> 0;
		let value = state;
		value = Math.imul(value ^ (value >>> 15), value | 1);
		value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
		return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
	};
};

export const pick = <T>(rng: Rng, items: readonly T[]): T => items[Math.floor(rng() * items.length)];

const shuffled = <T>(rng: Rng, items: readonly T[]): T[] => {
	const out = [...items];
	for (let i = out.length - 1; i > 0; i--) {
		const j = Math.floor(rng() * (i + 1));
		[out[i], out[j]] = [out[j], out[i]];
	}
	return out;
};

export const earlier = (a: DateTime, b: DateTime): DateTime => (a < b ? a : b);
export const later = (a: DateTime, b: DateTime): DateTime => (a > b ? a : b);
export const byTime = (a: DateTime, b: DateTime): number => a.toMillis() - b.toMillis();

export const shiftTypeOf = (request: ShiftRequest): ShiftType | null => Object.values(ShiftType).find((type) => type === request.message.shift_type) ?? null;

const factorOf = (rng: Rng, template: Template): OutreachFactor => {
	const { section, strength } = template;
	return "counts" in template ? { section, strength, kind: template.kind, count: pick(rng, template.counts) } : { section, strength, kind: template.kind };
};

/** Draws from `pool` without reusing a family already in `taken`. */
const drawFamilies = (rng: Rng, pool: readonly Template[], count: number, taken: Set<string>): Template[] => {
	const out: Template[] = [];
	for (const template of shuffled(rng, pool)) {
		if (out.length === count) break;
		if (taken.has(template.family)) continue;
		taken.add(template.family);
		out.push(template);
	}
	return out;
};

const matchOf = (factors: readonly OutreachFactor[]): OutreachMatch => {
	if (isAskLast(factors)) return OutreachMatch.LongShot;
	const total = factors.reduce((sum, factor) => sum + factor.strength, 0);
	if (total >= 5) return OutreachMatch.Great;
	if (total >= 3) return OutreachMatch.Good;
	if (total >= 1) return OutreachMatch.Fair;
	if (total === 0) return OutreachMatch.Maybe;
	return OutreachMatch.LongShot;
};

/**
 * `nurses`: everyone the department knows, active here or not, so a sign-up from off the roster still has a role.
 * `autoApprove`: whether the department auto-approves when the demo is seeded, which decides how past approvals happened.
 * `autoApproveRules`: the department's rules, which decide whether a past approval was one auto-approve would have made.
 */
export type DemoContext = {
	nurses: readonly Nurse[];
	nameOf: (nurseId: number) => string;
	timezone: string;
	now: DateTime;
	autoApprove: boolean;
	autoApproveRules: readonly AutoApproveRule[];
};

const shiftHoursOf = (request: ShiftRequest): number => request.message.shift_end.diff(request.message.shift_start, "hours").hours;

/**
 * The factors the schedule can answer for itself: a matching shift-type preference, how often they worked these hours,
 * and their hours that week with this shift. Each family it answers is taken, so the seeded ones never contradict it.
 */
const realFactorsOf = (request: ShiftRequest, nurse: Nurse, facts: NurseFacts | undefined, taken: Set<string>): OutreachFactor[] => {
	const factors: OutreachFactor[] = [];
	const shiftType = shiftTypeOf(request);
	const real = (section: OutreachSection, kind: OutreachCountedFactorKind, strength: OutreachFactorStrength, count: number) =>
		factors.push({ section, kind, strength, count });
	taken.add("pref");
	if (shiftType !== null && nurse.shiftType === shiftType) factors.push({ section: S.Preferences, kind: K.PrefersShiftType, strength: 2, shiftType });
	if (facts?.sameHoursCount != null) {
		taken.add("worked");
		const count = facts.sameHoursCount;
		if (count >= STRONG_WORKED_COUNT) real(S.WorkHistory, K.WorkedSameHours, 2, count);
		else if (count > 0) real(S.WorkHistory, K.WorkedSameHours, 1, count);
		else factors.push({ section: S.WorkHistory, kind: K.NotWorkedHoursLately, strength: -1 });
	}
	if (facts?.weekHours != null) {
		taken.add("hours");
		const { weekHours } = facts;
		const withShift = weekHours + shiftHoursOf(request);
		if (withShift > WEEKLY_OVERTIME_HOURS) real(S.WeeklyHours, K.OvertimeThatWeek, -2, Math.round(withShift - WEEKLY_OVERTIME_HOURS));
		else if (weekHours >= HIGH_WEEK_HOURS) real(S.WeeklyHours, K.HighHoursThatWeek, -1, Math.round(weekHours));
		else if (weekHours > 0 && weekHours <= LOW_WEEK_HOURS) real(S.WeeklyHours, K.LowHoursThatWeek, 1, Math.round(weekHours));
	}
	return factors;
};

/** Real factors first; how they answer offers and how often we've notified them have no client-side source, so they're seeded. */
const scoredPersonOf = (request: ShiftRequest, nurse: Nurse, ctx: DemoContext, askLast: boolean, facts: NurseFacts | undefined): OutreachScoredPerson => {
	const rng = rngOf(`${request.id}:${nurse.id}`);
	const taken = new Set<string>();
	const real = realFactorsOf(request, nurse, facts, taken);
	const templates = [
		...(askLast ? drawFamilies(rng, ASK_LAST, 1, taken) : []),
		...drawFamilies(rng, STRONG_HELPS, rng() < STRONG_SEEDED_SHARE ? 1 : 0, taken),
		...drawFamilies(rng, HELPS, Math.floor(rng() * (MAX_SEEDED_HELPS + 1)), taken),
		...drawFamilies(rng, DRAGS, Math.floor(rng() * (MAX_SEEDED_DRAGS + 1)), taken),
	];
	const factors = [...real, ...templates.map((template) => factorOf(rng, template))];
	return { nurseId: nurse.id, name: ctx.nameOf(nurse.id), match: matchOf(factors), factors };
};
const totalOf = (person: OutreachScoredPerson): number => person.factors.reduce((sum, factor) => sum + factor.strength, 0);

/** Agent ranking: best total first, ask-last people at the very end. */
export const byRank = (a: OutreachScoredPerson, b: OutreachScoredPerson): number => {
	const askLast = Number(isAskLast(a.factors)) - Number(isAskLast(b.factors));
	return askLast !== 0 ? askLast : totalOf(b) - totalOf(a) || a.nurseId - b.nurseId;
};

/** Great and Good matches go first: they get the first half of the text days, and the first calls. */
export const isBestMatch = (person: OutreachScoredPerson): boolean => person.match === OutreachMatch.Great || person.match === OutreachMatch.Good;

const atSendHour = (at: DateTime): DateTime => at.set({ hour: SEND_HOUR, minute: 0, second: 0, millisecond: 0 });

/** When each level starts on its own. */
export type LevelStarts = Record<OutreachLevel, DateTime>;

/**
 * The plan's own schedule. Texts start `LEAD_DAYS` before the shift (or `MIN_HISTORY_DAYS` ago, so a shift far off
 * still has a history); halfway to the calls, texts open to everyone who fits. Calls, overtime and the agency follow at
 * fixed points before the shift. Always in level order.
 */
export const automaticStartsOf = (start: DateTime, now: DateTime): LevelStarts => {
	const text = atSendHour(earlier(start.minus({ days: LEAD_DAYS }), now.minus({ days: MIN_HISTORY_DAYS })));
	const calls = start.minus({ days: CALLS_DAYS_BEFORE });
	const textDays = Math.max(0, Math.ceil(calls.diff(text, "days").days));
	return {
		[OutreachLevel.Text]: text,
		[OutreachLevel.MoreTexts]: text.plus({ days: Math.ceil(textDays / 2) }),
		[OutreachLevel.Calls]: calls,
		[OutreachLevel.Overtime]: start.minus({ days: OVERTIME_DAYS_BEFORE }),
		[OutreachLevel.Agency]: start.minus({ hours: AGENCY_HOURS_BEFORE }),
	};
};

/** The send slot (10:00 department time) on each day from `from` up to `until`, soonest first. */
const dailySlots = (from: DateTime, until: DateTime): DateTime[] => {
	const slots: DateTime[] = [];
	for (let slot = atSendHour(from); slot < until; slot = slot.plus({ days: 1 })) if (slot >= from) slots.push(slot);
	return slots;
};

type PlannedSend = { via: OutreachPlannedBatch["via"]; at: DateTime; people: OutreachScoredPerson[] };

/** `people`, in rank order, spread evenly over `slots`. With no slots nobody is texted, and they wait for the calls. */
const spread = (people: readonly OutreachScoredPerson[], slots: readonly DateTime[]): PlannedSend[] => {
	const bySlot = new Map<number, OutreachScoredPerson[]>();
	people.forEach((person, index) => {
		const slot = Math.floor((index * slots.length) / people.length);
		const group = bySlot.get(slot);
		if (group === undefined) bySlot.set(slot, [person]);
		else group.push(person);
	});
	return [...bySlot.entries()].map(([slot, group]) => ({ via: OutreachVia.Text, at: slots[slot], people: group }));
};

/** Best matches over the Text level's days, everyone else over More texts' days, and one app alert as More texts starts. */
const textsAndAlertOf = (ranked: readonly OutreachScoredPerson[], starts: LevelStarts, shiftStart: DateTime): PlannedSend[] => {
	const slots = dailySlots(starts[OutreachLevel.Text], starts[OutreachLevel.Calls]);
	const everyoneFrom = starts[OutreachLevel.MoreTexts];
	const alert: PlannedSend[] = everyoneFrom < shiftStart && ranked.length > 0 ? [{ via: OutreachVia.AppAlert, at: everyoneFrom, people: [...ranked] }] : [];
	return [
		...spread(
			ranked.filter(isBestMatch),
			slots.filter((slot) => slot < everyoneFrom),
		),
		...spread(
			ranked.filter((person) => !isBestMatch(person)),
			slots.filter((slot) => slot >= everyoneFrom),
		),
		...alert,
	].sort((a, b) => byTime(a.at, b.at));
};

/** Who gets called when: best matches first, one call after another, every 10 minutes, every 5 from `fastFrom`. */
export const callTimesOf = (people: readonly OutreachScoredPerson[], firstAt: DateTime, fastFrom: DateTime | null): OutreachPlannedCall[] => {
	const ordered = [...people].sort(byRank);
	const calls: OutreachPlannedCall[] = [];
	let at = firstAt;
	for (const person of [...ordered.filter(isBestMatch), ...ordered.filter((candidate) => !isBestMatch(candidate))]) {
		calls.push({ person, plannedAt: at });
		at = at.plus({ minutes: fastFrom !== null && at >= fastFrom ? CALL_GAP_MINUTES.fast : CALL_GAP_MINUTES.steady });
	}
	return calls;
};

/** Whether someone we asked said no: a seeded share of them. An acceptance is a sign-up, which outranks this. */
export const answerOf = (requestId: string, nurseId: number): OutreachAnswer =>
	rngOf(`${requestId}:${nurseId}:answer`)() * 100 < DECLINED_PERCENT ? OutreachAnswer.Declined : OutreachAnswer.NoAnswer;

export const scoredReach = (requestId: string, person: OutreachScoredPerson, via: OutreachVia): OutreachReachedPerson => ({
	kind: OutreachReachedKind.Scored,
	person,
	via,
	answer: answerOf(requestId, person.nurseId),
});

export const overtimeReach = (requestId: string, person: OutreachExcludedPerson, via: OutreachVia): OutreachReachedPerson => ({
	kind: OutreachReachedKind.Override,
	person,
	filter: OutreachExclusionFilter.DailyOvertime,
	via,
	answer: answerOf(requestId, person.nurseId),
});

const minutesIn = (rng: Rng, range: MinuteRange): number => range.min + Math.floor(rng() * (range.max - range.min));

const SIGN_UP_APPROVAL: Partial<Record<RequestResponsesStatusOptions, OutreachApproval>> = {
	[RequestResponsesStatusOptions.PENDING]: OutreachApproval.Waiting,
	[RequestResponsesStatusOptions.APPROVED]: OutreachApproval.ByManager,
	[RequestResponsesStatusOptions.DECLINED]: OutreachApproval.NotApproved,
};

/** Each person's latest standing from the request's real responses; an approval wins over an earlier response. */
const responsesOf = (request: ShiftRequest): Map<number, OutreachApproval> => {
	const byId = new Map<number, OutreachApproval>();
	for (const { nurse_id: nurseId, status } of request.message.request_data) {
		const approval = SIGN_UP_APPROVAL[status];
		if (approval === undefined) continue;
		if (byId.get(nurseId) !== OutreachApproval.ByManager) byId.set(nurseId, approval);
	}
	return byId;
};

/**
 * Who accepted, from the request's real responses: pending ones wait for a manager, approved ones were approved by one
 * (or on their own when auto-approve would have taken them), declined ones were turned down. Times and the week check are
 * seeded, a while after we reached them.
 */
const signUpsOf = (
	request: ShiftRequest,
	sentBatches: readonly OutreachSentBatch[],
	ctx: DemoContext,
	facts: ReadonlyMap<number, NurseFacts>,
): OutreachSignUp[] => {
	// Newest first, so the last write per person is when we first reached them.
	const reachedAt = new Map<number, DateTime>();
	for (const batch of sentBatches) for (const entry of batch.people) reachedAt.set(entry.person.nurseId, batch.sentAt);
	// DEFAULT: someone we never reached found the shift in the app; they count from when outreach began, or a day ago before it has.
	const outreachBegan = sentBatches.at(-1)?.sentAt ?? ctx.now.minus({ days: 1 });
	const nurseById = new Map(ctx.nurses.map((nurse) => [nurse.id, nurse]));
	return [...responsesOf(request).entries()]
		.map(([nurseId, approval]): OutreachSignUp => {
			const rng = rngOf(`${request.id}:${nurseId}:sign_up`);
			// DEFAULT: see `outreachBegan`.
			const signedUpAt = earlier((reachedAt.get(nurseId) ?? outreachBegan).plus({ minutes: minutesIn(rng, SIGN_UP_AFTER_MINUTES) }), ctx.now);
			const nurse = nurseById.get(nurseId);
			if (nurse === undefined) throw new Error(`Demo outreach: nurse ${nurseId} responded to request ${request.id} but isn't a nurse the department knows`);
			const known = facts.get(nurseId);
			const weekHours = known?.weekHours == null ? null : known.weekHours + shiftHoursOf(request);
			// DEFAULT: the real week and schedule when the shift's week is loaded and they're on the roster; otherwise the
			// hours, the overtime and the conflict are seeded, so the approval check still has something to show.
			const check = {
				role: nurse.role,
				hoursThatWeek: weekHours === null ? pick(rng, SIGN_UP_WEEK_HOURS) : Math.round(weekHours),
				overtimeHours:
					weekHours === null
						? rng() < SIGN_UP_OVERTIME_SHARE
							? pick(rng, SIGN_UP_OVERTIME_HOURS)
							: 0
						: Math.max(0, Math.round(weekHours - WEEKLY_OVERTIME_HOURS)),
				hasConflict: known?.hasConflict == null ? rng() < SIGN_UP_CONFLICT_SHARE : known.hasConflict,
			};
			const base = { nurseId, name: ctx.nameOf(nurseId), signedUpAt, check };
			if (approval === OutreachApproval.Waiting) return { ...base, approval, decidedAt: null };
			if (approval === OutreachApproval.ByManager && isAutoApproved(ctx, nurse, check))
				return { ...base, approval: OutreachApproval.Automatic, decidedAt: signedUpAt };
			return { ...base, approval, decidedAt: earlier(signedUpAt.plus({ minutes: minutesIn(rng, MANAGER_DECIDES_AFTER_MINUTES) }), ctx.now) };
		})
		.sort((a, b) => byTime(b.signedUpAt, a.signedUpAt));
};

/**
 * Whether auto-approve would have taken this sign-up itself: the nurse hasn't opted out and a rule lets its overtime
 * through. The rules' other gates (jobs, days, hours, lead time, shortage) aren't checked in the demo.
 */
const isAutoApproved = (ctx: DemoContext, nurse: Nurse, check: OutreachSignUpCheck): boolean =>
	ctx.autoApprove &&
	nurse.autoApproveEligible !== false &&
	// DEFAULT: an unset `maxOt` is a rule that allows no overtime (see `AutoApproveRule.maxOt`).
	ctx.autoApproveRules.some((rule) => check.overtimeHours <= (rule.maxOt ?? 0));

const DAILY_OVERTIME = OutreachExclusionFilter.DailyOvertime;

/** The plan played out up to now: texts and the app alert, the calls, and the overtime offers once that level has passed. */
const historyOf = (request: ShiftRequest, ranked: OutreachScoredPerson[], groups: OutreachExcludedGroup[], starts: LevelStarts, now: DateTime): History => {
	const sends = textsAndAlertOf(ranked, starts, request.message.shift_start);
	const pastCalls = callTimesOf(ranked, starts[OutreachLevel.Calls], starts[OutreachLevel.Overtime]).filter((call) => call.plannedAt < now);
	const overtimeAt = starts[OutreachLevel.Overtime];
	const overtimePeople = overtimeAt < now ? groups.filter((group) => group.filter === DAILY_OVERTIME).flatMap((group) => group.people) : [];
	const sentBatches: OutreachSentBatch[] = [
		...sends
			.filter((entry) => entry.at < now)
			.map((entry, index) => ({
				id: `${entry.via}-${index}`,
				sentAt: entry.at,
				byUser: false,
				people: entry.people.map((person) => scoredReach(request.id, person, entry.via)),
			})),
		// One batch per call, so each lands at its own time; Reached groups them by the hour.
		...pastCalls.map((call) => ({
			id: `call-${call.person.nurseId}`,
			sentAt: call.plannedAt,
			byUser: false,
			people: [scoredReach(request.id, call.person, OutreachVia.Call)],
		})),
		...(overtimePeople.length > 0
			? [{ id: "overtime", sentAt: overtimeAt, byUser: false, people: overtimePeople.map((person) => overtimeReach(request.id, person, OutreachVia.Call)) }]
			: []),
	].sort((a, b) => byTime(b.sentAt, a.sentAt));
	const plannedBatches: OutreachPlannedBatch[] = sends
		.filter((entry) => entry.at >= now)
		.map((entry, index) => ({ id: `planned-${index}`, via: entry.via, plannedAt: entry.at, people: entry.people }));
	// Overtime offers went out, so those people are no longer left out.
	const excludedGroups = overtimePeople.length > 0 ? groups.filter((group) => group.filter !== DAILY_OVERTIME) : groups;
	return { sentBatches, plannedBatches, excludedGroups };
};

export type SeededOutreach = {
	ranked: OutreachScoredPerson[];
	starts: LevelStarts;
	sentBatches: OutreachSentBatch[];
	plannedBatches: OutreachPlannedBatch[];
	excludedGroups: OutreachExcludedGroup[];
	signUps: OutreachSignUp[];
};

type History = Pick<SeededOutreach, "sentBatches" | "plannedBatches" | "excludedGroups">;

/**
 * Someone who already responded is past asking: they leave the planned sends and the left-out groups (so no overtime
 * offer or Text anyway either). What we sent them before stays, so Reached shows how they came to accept.
 */
const withoutResponders = (history: History, responded: ReadonlySet<number>): History => ({
	sentBatches: history.sentBatches,
	plannedBatches: history.plannedBatches
		.map((batch) => ({ ...batch, people: batch.people.filter((person) => !responded.has(person.nurseId)) }))
		.filter((batch) => batch.people.length > 0),
	excludedGroups: history.excludedGroups
		.map((group) => ({ ...group, people: group.people.filter((person) => !responded.has(person.nurseId)) }))
		.filter((group) => group.people.length > 0),
});

/** `split`: the real roster split (`splitRoster`) — who can be asked, who is left out and why, and what the schedule says. */
export const seedOutreach = (request: ShiftRequest, ctx: DemoContext, split: RosterSplit): SeededOutreach => {
	const rng = rngOf(request.id);
	const { eligible, groups, facts } = split;
	const askLastIds = new Set(
		shuffled(rng, eligible)
			.slice(0, ASK_LAST_PEOPLE)
			.map((nurse) => nurse.id),
	);
	const ranked = eligible.map((nurse) => scoredPersonOf(request, nurse, ctx, askLastIds.has(nurse.id), facts.get(nurse.id))).sort(byRank);
	const now = ctx.now.setZone(ctx.timezone);
	const starts = automaticStartsOf(request.message.shift_start.setZone(ctx.timezone), now);
	const history = withoutResponders(historyOf(request, ranked, groups, starts, now), new Set(responsesOf(request).keys()));
	return { ranked, starts, ...history, signUps: signUpsOf(request, history.sentBatches, ctx, facts) };
};
